import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '../context/LanguageContext';
import { useGuest } from '../context/GuestContext';
import '../styles/RSVPForm.css';

const RSVPForm = () => {
  const { t } = useTranslation();
  const { guest } = useGuest();

  const [formData, setFormData] = useState({
    email: '',
    countryCode: '+36',
    phone: '',
    attending: '',
    plusOneName: '',
    dietaryRestrictions: '',
    message: ''
  });

  const [submitError, setSubmitError] = useState('');

  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const totalSteps = 3;

  const countryCodes = [
    { code: '+36', country: 'HU' },
    { code: '+44', country: 'UK' },
    { code: '+61', country: 'AU' },
    { code: '+1', country: 'US/CA' },
    { code: '+43', country: 'AT' },
    { code: '+55', country: 'BR' },
    { code: '+86', country: 'CN' },
    { code: '+420', country: 'CZ' },
    { code: '+33', country: 'FR' },
    { code: '+49', country: 'DE' },
    { code: '+91', country: 'IN' },
    { code: '+39', country: 'IT' },
    { code: '+81', country: 'JP' },
    { code: '+82', country: 'KR' },
    { code: '+31', country: 'NL' },
    { code: '+48', country: 'PL' },
    { code: '+40', country: 'RO' },
    { code: '+421', country: 'SK' },
    { code: '+34', country: 'ES' },
    { code: '+41', country: 'CH' }
  ];

  const validateField = (name, value) => {
    switch (name) {
      case 'email':
        if (!value.trim()) return t('rsvp.emailRequired');
        if (!/\S+@\S+\.\S+/.test(value)) return t('rsvp.emailInvalid');
        return '';
      case 'phone':
        if (value && !/^[+]?[0-9\s\-()]+$/.test(value)) return t('rsvp.phoneInvalid');
        return '';
      case 'attending':
        if (!value) return t('rsvp.attendRequired');
        return '';
      default:
        return '';
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = t('rsvp.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('rsvp.emailInvalid');
    }

    if (!formData.attending) {
      newErrors.attending = t('rsvp.attendRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    const fieldError = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: fieldError }));
  };

  const GOOGLE_SCRIPT_URL = process.env.REACT_APP_RSVP_URL || 'https://script.google.com/macros/s/AKfycbyAVVdNDxZwTO8l007FU0TSJ0vF5GnbB_CD10ElREooXoff86xK0-YfrPL-it6e8GmM/exec';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (currentStep !== totalSteps) return;
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitError('');

    const payload = {
      firstName: guest.firstName,
      surname: guest.surname,
      attending: formData.attending,
      email: formData.email,
      phone: formData.phone,
      countryCode: formData.countryCode,
      plusOneName: guest.plusOne ? formData.plusOneName : '',
      dietaryRestrictions: formData.dietaryRestrictions,
      message: formData.message,
    };

    try {
      const response = await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (data.success) {
        setIsSubmitted(true);
      } else {
        setSubmitError(t('rsvp.submitError'));
      }
    } catch (error) {
      console.error('Error submitting RSVP:', error);
      setSubmitError(t('rsvp.submitError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextStep = (e) => {
    if (e) e.preventDefault();

    if (currentStep === 1) {
      const step1Errors = {};
      const attendingError = validateField('attending', formData.attending);
      if (attendingError) step1Errors.attending = attendingError;
      if (Object.keys(step1Errors).length > 0) {
        setErrors(step1Errors);
        return;
      }
    } else if (currentStep === 2) {
      const step2Errors = {};
      const emailError = validateField('email', formData.email);
      const phoneError = validateField('phone', formData.phone);
      if (emailError) step2Errors.email = emailError;
      if (phoneError) step2Errors.phone = phoneError;
      if (Object.keys(step2Errors).length > 0) {
        setErrors(step2Errors);
        return;
      }
    }

    setErrors({});
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const displayName = guest?.displayName || guest?.firstName;

  const getTitle = () => {
    if (guest?.special === 'amiker') return "You're literally the bride";
    if (guest?.special === 'gergely') return "You better be attending";
    return t('rsvp.title');
  };

  const getSubtitle = () => {
    if (guest?.special === 'amiker') return "No RSVP needed. You're kind of required to be there.";
    if (guest?.special === 'gergely') return "This is not optional. She will find you.";
    return t('rsvp.reserveYourSpot');
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <motion.div
            key="step1"
            initial={{ x: 300, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -300, opacity: 0 }}
            className="form-step"
          >
            <h3>{t('rsvp.step2Title')}</h3>
            <div className="form-group">
              <label>{t('rsvp.willAttend')} *</label>
              <div className="radio-group" role="radiogroup" aria-required="true">
                <label className={`radio-label ${formData.attending === 'yes' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="attending"
                    value="yes"
                    checked={formData.attending === 'yes'}
                    onChange={handleInputChange}
                  />

                  {t('rsvp.yesAttend')}
                </label>
                <label className={`radio-label ${formData.attending === 'no' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="attending"
                    value="no"
                    checked={formData.attending === 'no'}
                    onChange={handleInputChange}
                  />

                  {t('rsvp.noAttend')}
                </label>
              </div>
              {errors.attending && <span className="error-message">{errors.attending}</span>}
            </div>

            {guest?.plusOne && formData.attending === 'yes' && (
              <div className="form-group">
                <label>{t('rsvp.plusOneName')}</label>
                <input
                  type="text"
                  name="plusOneName"
                  value={formData.plusOneName}
                  onChange={handleInputChange}
                  placeholder={t('rsvp.plusOnePlaceholder')}
                />
              </div>
            )}
          </motion.div>
        );

      case 2:
        return (
          <motion.div
            key="step2"
            initial={{ x: 300, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -300, opacity: 0 }}
            className="form-step"
          >
            <h3>{t('rsvp.step1Title')}</h3>

            <div className="form-group">
              <label>{t('rsvp.email')} *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={errors.email ? 'error' : ''}
                placeholder={t('rsvp.emailPlaceholder')}
                required
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label>{t('rsvp.phone')}</label>
              <div className="phone-input-group">
                <select
                  name="countryCode"
                  value={formData.countryCode}
                  onChange={handleInputChange}
                  className="country-code-select"
                >
                  {countryCodes.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.country} {country.code}
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={errors.phone ? 'error phone-input' : 'phone-input'}
                  placeholder={t('rsvp.phonePlaceholder')}
                />
              </div>
              {errors.phone && <span className="error-message">{errors.phone}</span>}
            </div>
          </motion.div>
        );

      case 3:
        return (
          <motion.div
            key="step3"
            initial={{ x: 300, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -300, opacity: 0 }}
            className="form-step"
          >
            <h3>{t('rsvp.step3Title')}</h3>
            <div className="form-group">
              <label>{t('rsvp.dietary')}</label>
              <textarea
                name="dietaryRestrictions"
                value={formData.dietaryRestrictions}
                onChange={handleInputChange}
                placeholder={t('rsvp.dietaryPlaceholder')}
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>{t('rsvp.messageLabel')}</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder={t('rsvp.messagePlaceholder')}
                rows="4"
              />
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  if (isSubmitted) {
    return (
      <section id="rsvp-section" className="rsvp-section">
        <motion.div
          className="rsvp-section-inner"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="rsvp-container">
            <div className="success-message">
              <motion.div
                className="success-checkmark"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 300 }}
              >
                &#10003;
              </motion.div>
              <h2>{t('rsvp.thankYou')}</h2>
              <p>{t('rsvp.received')}</p>
            </div>
          </div>
        </motion.div>
      </section>
    );
  }

  return (
    <section id="rsvp-section" className="rsvp-section">
      <motion.div
        className="rsvp-header"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="rsvp-title">{getTitle()}</h2>
        <p className="rsvp-subtitle">{getSubtitle()}</p>
      </motion.div>

      <motion.div
        className="rsvp-section-inner"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <div className="rsvp-container">
          <div className="rsvp-guest-badge">
            <span className="rsvp-guest-name">{displayName} {guest?.surname}</span>
          </div>

          <div className="progress-bar">
            <div className="progress-track">
              <motion.div
                className="progress-fill"
                initial={{ width: 0 }}
                animate={{ width: `${(currentStep / totalSteps) * 100}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
            <div className="step-indicators">
              {[1, 2, 3].map(step => (
                <motion.div
                  key={step}
                  className={`step-indicator ${step <= currentStep ? 'active' : ''}`}
                  whileHover={{ scale: 1.1 }}
                >
                  {step}
                </motion.div>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="rsvp-form">
            <AnimatePresence mode="wait">
              {renderStep()}
            </AnimatePresence>

            <div className="form-navigation">
              {currentStep > 1 && (
                <motion.button
                  type="button"
                  onClick={prevStep}
                  className="btn-secondary"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {t('rsvp.previous')}
                </motion.button>
              )}

              {currentStep < totalSteps ? (
                <motion.button
                  type="button"
                  onClick={nextStep}
                  className="btn-primary"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {t('rsvp.next')}
                </motion.button>
              ) : (
                <motion.button
                  type="submit"
                  className="btn-primary submit-btn"
                  disabled={isSubmitting}
                  whileHover={{ scale: isSubmitting ? 1 : 1.05 }}
                  whileTap={{ scale: isSubmitting ? 1 : 0.95 }}
                >
                  {isSubmitting ? (
                    <motion.div
                      className="loading-spinner"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    >
                      ...
                    </motion.div>
                  ) : (
                    t('rsvp.submit')
                  )}
                </motion.button>
              )}
            </div>

            {submitError && (
              <motion.p
                className="submit-error"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {submitError}
              </motion.p>
            )}
          </form>
        </div>
      </motion.div>
    </section>
  );
};

export default RSVPForm;
